<template>
    <div v-if="user" id="licenses-view-container" class="align-self-center h-full w-full" style="margin-top: 2.5rem; max-width: 75rem;">
    
        <div v-if="activationsToMigrateLoaded">
            <div class="w-full h-full m-0 p-0 flex flex-column gap-1">

                <span class="mb-3" style="color: var(--theme-font-color);">
                    Below are your active licenses ready for migration. Click the
                    <span style="font-weight: var(--font-weight-bold);">Migrate</span>
                    button to update them to the new activation system, linking them to your account for easier management.
                </span>

                <div class="licenses-table mt-0 w-full">
                    <DataTable class="datatable-license mb-2" alwaysShowPaginator="" :value="myActivationsToMigrate" size="small" tableStyle="min-width: 100%" :row-class="() => 'row'" scrollable scrollHeight="flex" style="max-height: 25.5rem;"
                        sortMode="single" sortField="serial_number" :sortOrder="1" row resizableColumns columnResizeMode="fit"  
                    >
                        <template #empty>
                            <div class="w-full flex flex-column justify-content-center align-items-center p-3" style="background-color: var(--table-row-color)">
                                <span>It seems there is no activations to migrate.</span>
                            </div>
                        </template>
                        
                        <Column field="serial_number" style="width: 30%;" class="resize">
                            <template #header>
                                <div class="w-full flex flex-row align-items-center ml-2">
                                    <span class="p-column-title">Serial Number</span>
                                </div>
                            </template>
                            <template #body="slotProps">
                                <div class="flex flex-row align-items-center ml-2">
                                    <span class="potentially-long-label text-serial-number">{{slotProps.data.serial_number}}</span>
                                </div>
                            </template>
                        </Column>
                        <Column field="product" style="max-width: 55%;" class="resize">
                            <template #header>
                                <div class="w-full flex flex-row align-items-center ml-2">
                                    <span class="p-column-title">Description</span>
                                </div>
                            </template>
                            <template #body="slotProps">
                                <div class="relative w-full" style="margin-top: 0.6rem; height: 2rem;">
                                    <ScrollPanel class="horizontal-scrollpanel-table horizontal-scrollpanel-table-description absolute">
                                        <span>{{slotProps.data.description}}</span>
                                    </ScrollPanel>
                                </div>
                            </template>
                        </Column>
                        <Column field="product" style="width: 25%;" class="resize">
                            <template #header>
                                <div class="w-full flex flex-row align-items-center justify-content-center">
                                    <span class="p-column-title">Migration Status</span>
                                </div>
                            </template>
                            <template #body="slotProps">
                                <div class="flex flex-row align-items-center justify-content-center">
                                    <div v-if="httpMigrateLoading">
                                        <LoadingSpinner style="height: 1rem;"/>
                                    </div>
                                    <div v-else>
                                        <MigrationStatusTag :migrationStatusInfo="slotProps.data"/>
                                    </div>
                                </div>
                            </template>
                        </Column>
                    </DataTable>
                </div>

                <div class="flex flex-row align-items-center justify-content-end">
                    <ButtonModule type="primary" severity="normal" :label="!(migrationInProgress || httpMigrateLoading || httpMigrationPreparationLoading) ? 'Migrate' : 'Wait...'" @click="clickedMigrate" :disabled="migrationInProgress || httpMigrateLoading || httpMigrationPreparationLoading || !canMigrate"/>
                </div>
            </div>

            <!-- DIALOGS -->

        </div>
        <div v-else style="width: 100%; height: 100%;" class="flex justify-content-center align-items-center">
            <LoadingSpinner/>
        </div>
    </div>
    <div v-else style="margin-top: 2.5rem;">
        <span>You must sign in to have access to the dashboard with your licenses.</span>
    </div>
</template>

<script setup>
// --- IMPORTS ---
import { ref, computed, onMounted } from 'vue';
// PrimeVue
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import ScrollPanel from 'primevue/scrollpanel';
// Vue
import LoadingSpinner from '@/components/Modules/LoadingSpinner.vue';
import ButtonModule from '@/components/Modules/ButtonModule.vue';
import MigrationStatusTag from '@/components/Modules/MigrationStatusTag.vue'
// Others
import toastService from '@/composables/toastService';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router'
import UserUtils from '@/composables/UserUtils';

const store = useStore()
const router = useRouter()
const { logoutUser } = UserUtils(store, router)
store.commit('applicationWebView/setIsWebView')

// API
import useApiRequest from '@/composables/HTTPS/useApiRequest';
const { fetchGetApplicationInfo, fetchMigrate, fetchMigrationPreparation } = useApiRequest()
const httpGetApplicationInfoLoading = ref(false)
const httpGetApplicationInfoData = ref(null)
const httpMigrateLoading = ref(false)
const httpMigrateData = ref(null)
const httpMigrationPreparationLoading = ref(false)
const httpMigrationPreparationData = ref(null)

// --- Application and machine info ---
const user = computed(() => store.state.user.user)
const applicationCode = computed(() => store.state.applicationWebView.applicationCode)

// --- LOAD APPLICATION INFO FROM THE SERVER ---
const loadApplicationLicenses = async () => {
    httpGetApplicationInfoLoading.value = true
    try {
        httpGetApplicationInfoData.value = await fetchGetApplicationInfo({ application_code: applicationCode.value })
        store.commit('applicationWebView/setApplicationGlobalInfo', httpGetApplicationInfoData.value.application_info)
    } catch (err) {
        handleGetApplicationInfoError(err)
    } finally {
        httpGetApplicationInfoLoading.value = false
    }
}
// Handle an error from the server
const handleGetApplicationInfoError = (error) => {
    try {
        if (error.response == null) { // If there is no .response key, then it means it is not an error from the server, it did not reach it
            toastService.add({ severity: 'error', summary: `Get Application Information: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
            return;
        }
        const errorStatus = error.response.status
        const errorCode = error.response.data.error_code
        const errorMessage = error.response.data.error_message
        switch (errorStatus) {
            case 400: // MISSING_OBJ / ACCESS_TOKEN_MISSING / MISSING_OBJ_ELEM (400)
                if (errorCode == 'ACCESS_TOKEN_MISSING') {
                    toastService.add({ severity: 'error', summary: "Get Application Information: Authorization Issue.", detail: 'You are not logged in.', life: 5000 });
                    // Logout
                    logoutUser()
                }
                else {  toastService.add({ severity: 'error', summary: `Get Application Information: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            case 401: // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID (401)
                if (errorCode == 'ACCESS_TOKEN_EXPIRED' || errorCode == 'ACCESS_TOKEN_INVALID') {
                    // Logout: Do nothing, the interceptor should have made a refresh token request!
                    // logoutUser()
                }
                else {  toastService.add({ severity: 'error', summary: `Get Application Information: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            case 404: // ACCOUNT_NOT_FOUND / INVALID_APPLICATION (404)
                if (errorCode == 'ACCOUNT_NOT_FOUND') {
                    toastService.add({ severity: 'error', summary: "Get Application Information: Account Not Found.", detail: 'The account no more exists.', life: 5000 });
                    // Logout
                    logoutUser()
                }
                else if (errorCode == 'INVALID_APPLICATION') {
                    toastService.add({ severity: 'error', summary: "Get Application Information: Invalid application.", detail: errorMessage, life: 5000 });
                }
                else {  toastService.add({ severity: 'error', summary: `Get Application Information: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            default:
                toastService.add({ severity: 'error', summary: `Get Application Information: Error ${errorStatus}.`, detail: errorMessage, life: 5000 });
                break;
        }
    } catch {
        toastService.add({ severity: 'error', summary: `Get Application Information: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
    }
}

// --- SHOW THE ACTIVATIONS TO MIGRATE ---
const myActivationsToMigrate = ref([])
// (1) FROM JS TO OBJC : Ask the activations information
const makeMigrationPreparation = () => {
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.jsMigrationPreparation) {
        window.webkit.messageHandlers.jsMigrationPreparation.postMessage(null);
    }
}
// (2.1) FROM OBJC TO JS : ObjC app responds with the activations to migrate
window.appSentActivationsToMigrate = async (activationsToMigrateJsonString) => {
    try {
        // Parse the JSON string to get the array of dictionaries
        myActivationsToMigrate.value = JSON.parse(activationsToMigrateJsonString).map(activation => ({
            serial_number: activation.serial_number,
            description: activation.description,
            migration_status: '',
            error_message: ''
        }));
    } catch (e) {
        // Issue... do nothing then
    }
};
// (2.2) FROM OBJC TO JS : ObjC app responds with the migration preparation record
window.appSentMigrationPreparationRecord = async (migrationPreparationRecord) => {
    // (3) FROM JS TO SERVER : Make the migration preparation request to the server
    httpMigrationPreparationLoading.value = true
    try {
        httpMigrationPreparationData.value = await fetchMigrationPreparation({ migration_preparation_record: migrationPreparationRecord })
        // The server responds an error in case of a "global" error such as an invalid token for example. But concerning a specific license error, it is sent in the response object (the response is successful but can contain error information for a specific license migration) => all handled in updateMigrationPreparationStatus)

        // Now that we loaded the migration preparation status, we update it in our variable
        updateMigrationPreparationStatus(httpMigrationPreparationData.value.migration_preparation_status)
    } catch (err) {
        handleMigrationPreparationError(err)
    } finally {
        httpMigrationPreparationLoading.value = false
        if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.jsMigrationPreparationDone) {
            window.webkit.messageHandlers.jsMigrationPreparationDone.postMessage(null);
        }
    }
};
// Handle an error from the server
const handleMigrationPreparationError = (error) => {
    try {
        if (error.response == null) { // If there is no .response key, then it means it is not an error from the server, it did not reach it
            toastService.add({ severity: 'error', summary: `Migration Preparation: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
            return;
        }
        const errorStatus = error.response.status
        const errorCode = error.response.data.error_code
        const errorMessage = error.response.data.error_message
        switch (errorStatus) {
            case 400: // MISSING_OBJ / ACCESS_TOKEN_MISSING / MISSING_OBJ_ELEM (400)
                if (errorCode == 'ACCESS_TOKEN_MISSING') {
                    toastService.add({ severity: 'error', summary: "Migration Preparation: Authorization Issue.", detail: 'You are not logged in.', life: 5000 });
                    // Logout
                    logoutUser()
                }
                else {  toastService.add({ severity: 'error', summary: `Migration Preparation: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            case 401: // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID (401)
                if (errorCode == 'ACCESS_TOKEN_EXPIRED' || errorCode == 'ACCESS_TOKEN_INVALID') {
                    // Logout: Do nothing, the interceptor should have made a refresh token request!
                    // logoutUser()
                }
                else {  toastService.add({ severity: 'error', summary: `Migration Preparation: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            case 404: // ACCOUNT_NOT_FOUND (404)
                if (errorCode == 'ACCOUNT_NOT_FOUND') {
                    toastService.add({ severity: 'error', summary: "Migration Preparation: Account Not Found.", detail: 'The account no more exists.', life: 5000 });
                    // Logout
                    logoutUser()
                }
                else {  toastService.add({ severity: 'error', summary: `Migration Preparation: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            default:
                toastService.add({ severity: 'error', summary: `Migration Preparation: Error ${errorStatus}.`, detail: errorMessage, life: 5000 });
                break;
        }
    } catch {
        toastService.add({ severity: 'error', summary: `Migration Preparation: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
    }
}
const updateMigrationPreparationStatus = (migrationPreparationStatusArray) => {
    migrationPreparationStatusArray.forEach((migrationPreparationStatus) => {
        if (!migrationPreparationStatus.migration_status) {
            //toastService.add({ severity: 'warn', summary: `Migration Preparation Status: ${migrationPreparationStatus.serial_number}`, detail: migrationPreparationStatus.message, life: 5000 });
        }
        // Set the value for migration_status
        myActivationsToMigrate.value = myActivationsToMigrate.value.map((activation) => {
            if (migrationPreparationStatus.serial_number === activation.serial_number) {
                if (migrationPreparationStatus.description && migrationPreparationStatus.description.length>0) {
                    return { ...activation, 'description': migrationPreparationStatus.description, 'migration_status': migrationPreparationStatus.migration_status ? 'MIGRATION_READY' : 'MIGRATION_NOT_READY', 'error_message' : migrationPreparationStatus.message }
                }
                return { ...activation, 'migration_status': migrationPreparationStatus.migration_status ? 'MIGRATION_READY' : 'MIGRATION_NOT_READY', 'error_message' : migrationPreparationStatus.message }
            }
            return activation
        })
    })
}

// Can migrate if : (1) At least something to migrate, (2) Only if there is only licenses that are migrated or ready to migrate (no "can't migrate"), (3) If there is at least one license ready to migrate
const canMigrate = computed(() => myActivationsToMigrate.value.length>0 && !myActivationsToMigrate.value.some((activation) => activation.migration_status !== 'MIGRATION_SUCCESSFUL' && activation.migration_status !== 'MIGRATION_READY') && myActivationsToMigrate.value.some((activation) => activation.migration_status === 'MIGRATION_READY'))

// On Mounted : load the application information (from server) + activations to migrate information from the ObjC
const activationsToMigrateLoaded = ref(false)
onMounted(() => {
    // Set WebView size
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.jsResizeWindow) {
        window.webkit.messageHandlers.jsResizeWindow.postMessage({ width: 1050, height: 720 });
    }
    loadApplicationLicenses()
    makeMigrationPreparation()
    activationsToMigrateLoaded.value= true
})


// --- MIGRATION ---
const migrationInProgress = ref(false)

// (1) FROM JS TO OBJC : Ask the migration record to the ObjC app.
const clickedMigrate = () => {
    migrationInProgress.value = true
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.jsMigrateAction) {
        window.webkit.messageHandlers.jsMigrateAction.postMessage(myActivationsToMigrate.value.map((activationToMigrate) => activationToMigrate.serial_number));
    }
}
// (1.1) FROM OBJ TO JS : ObjC cancels the migration because nothing to migrate or <System ID> file is wrong
window.appCancelledMigration = async () => {
    migrationInProgress.value = false
}
// (2) FROM OBJC TO JS : ObjC app responds with the migration record (it contains all the license to be migrated + the necessary information for new activation tokens)
window.appSentMigrationRecord = async (migrationRecord) => {
    // (3) FROM JS TO SERVER : Make the migration request to the server
    httpMigrateLoading.value = true
    try {
        httpMigrateData.value = await fetchMigrate({ migration_record: migrationRecord })

        const migratedActivations = httpMigrateData.value.migrated_activations

        const newActivations = []
        migratedActivations.forEach((migratedActivation) => {
            if (!migratedActivation.migration_status) { // this should not happen since the server is supposed to send a SUCCESS only if ALL activations have been migrated correctly
                toastService.add({ severity: 'error', summary: `Migration: Unsuccessful (${migratedActivation.serial_number})`, detail: migratedActivation.message, life: 5000 });
            }
            else { // If success, store the new activation token (to be sent to ObjC) !
                newActivations.push({
                    "serial_number": migratedActivation.serial_number,
                    "description": migratedActivation.description,
                    "activation_token": migratedActivation.activation_token
                })
            }
            // Set the value for migration_status
            if (!migratedActivation.migration_status) { // HERE, ONLY UPDATE IF ERROR ! => For the success, it will be notified by the ObjC when everything is fine locally!
                myActivationsToMigrate.value = myActivationsToMigrate.value.map((activation) => {
                    if (migratedActivation.serial_number === activation.serial_number) {
                        return { ...activation, ['migration_status']: migratedActivation.migration_status ? 'MIGRATION_SUCCESSFUL' : 'MIGRATION_ERROR' }
                    }
                    return activation
                })
            }
        })

        // (4) FROM JS TO OBJC : Tell the server response to the ObjC (thus send the new activation token with their respective serial numbers)
        if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.jsMigrationSuccess) {
            window.webkit.messageHandlers.jsMigrationSuccess.postMessage(newActivations);
        }
    } catch (err) {
        if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.jsMigrationFailed) {
            window.webkit.messageHandlers.jsMigrationFailed.postMessage(null);
        }
        handleMigrateError(err)
    } finally {
        httpMigrateLoading.value = false
        migrationInProgress.value = false
    }
};
// Handle an error from the server
const handleMigrateError = (error) => {
    try {
        if (error.response == null) { // If there is no .response key, then it means it is not an error from the server, it did not reach it
            toastService.add({ severity: 'error', summary: `Migration: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
            return;
        }
        const errorStatus = error.response.status
        const errorCode = error.response.data.error_code
        const errorMessage = error.response.data.error_message
        switch (errorStatus) {
            case 400: // MISSING_OBJ / ACCESS_TOKEN_MISSING / MISSING_OBJ_ELEM (400)
                if (errorCode == 'ACCESS_TOKEN_MISSING') {
                    toastService.add({ severity: 'error', summary: "Migration: Authorization Issue.", detail: 'You are not logged in.', life: 5000 });
                    // Logout
                    logoutUser()
                }
                else {  toastService.add({ severity: 'error', summary: `Migration: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            case 401: // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID / MIGRATION_ERROR (401)
                if (errorCode == 'ACCESS_TOKEN_EXPIRED' || errorCode == 'ACCESS_TOKEN_INVALID') {
                    // Logout: Do nothing, the interceptor should have made a refresh token request!
                    // logoutUser()
                }
                else if (errorCode == 'MIGRATION_ERROR') {
                    toastService.add({ severity: 'error', summary: `Migration: Unsuccessful`, detail: "Not all activations are migratable", life: 5000 });
                    // Since the migration is unsuccessful, it means one of the license (at least) is not migratable, which was not shown in the table... There is conflict between what the webview has and what the server stores => UPDATE
                    makeMigrationPreparation()
                }
                else {  toastService.add({ severity: 'error', summary: `Migration: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            case 404: // ACCOUNT_NOT_FOUND (404)
                if (errorCode == 'ACCOUNT_NOT_FOUND') {
                    toastService.add({ severity: 'error', summary: "Migration: Account Not Found.", detail: 'The account no more exists.', life: 5000 });
                    // Logout
                    logoutUser()
                }
                else {  toastService.add({ severity: 'error', summary: `Migration: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            default:
                toastService.add({ severity: 'error', summary: `Migration: Error ${errorStatus}.`, detail: errorMessage, life: 5000 });
                break;
        }
    } catch {
        toastService.add({ severity: 'error', summary: `Migration: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
    }
}
// After JS sends new activations to ObjC, Objc must finish the migrations locally and then confirm to the JS for the displayed status
window.appMigrationSuccess = async (successfulMigrationInfo) => {
    const successfulMigrations = successfulMigrationInfo.successful_migrations

    // For each confirmed migration, change the status in the table !
    successfulMigrations.forEach((successfulMigration) => {
        myActivationsToMigrate.value = myActivationsToMigrate.value.map((activation) => {
            if (successfulMigration.serial_number === activation.serial_number) {
                return { ...activation, ['migration_status']: 'MIGRATION_SUCCESSFUL' }
            }
            return activation
        })
    })
    
}
</script>

<style scoped>
/* Columns header */
:deep(.p-resizable-column) {
    padding: 16px 5px;
}
:deep(.resize .p-column-resizer) {
    background-color: var(--table-column-separator-color);
    margin-top: 0.4rem;
    height: 80%;
    width: 0.03rem;
    margin-right: 0.1rem;
}
:deep(.row) {
    height: 2.5rem;
}
</style>